import React, { createContext, useContext, useState, ReactNode } from 'react';
import { UserInfo, SupportedLanguage } from '../types';

interface SettingsContextType {
  userInfo: UserInfo;
  setUserInfo: (info: React.SetStateAction<UserInfo>) => void;
  interfaceLanguage: SupportedLanguage;
  setInterfaceLanguage: (lang: SupportedLanguage) => void;
}

const defaultUserInfo: UserInfo = {
  nativeLanguage: 'English',
  targetLanguage: 'Portuguese',
  languageLevel: 'beginner',
  age: 25,
  conversationType: 'casual',
  speechSpeed: '1',
  autoPlayAudio: true
};

const SettingsContext = createContext<SettingsContextType | undefined>(undefined);

export const useSettings = () => {
  const context = useContext(SettingsContext);
  if (!context) {
    throw new Error('useSettings must be used within a SettingsProvider');
  }
  return context;
};

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({ children }) => {
  const [userInfo, setUserInfo] = useState<UserInfo>(defaultUserInfo);
  const [interfaceLanguage, setInterfaceLanguage] = useState<SupportedLanguage>('en');

  React.useEffect(() => {
    const savedSettings = localStorage.getItem('voicebot-settings');
    if (savedSettings) {
      const settings = JSON.parse(savedSettings);
      setUserInfo(settings.userInfo);
      setInterfaceLanguage(settings.interfaceLanguage);
    }
  }, []);

  React.useEffect(() => {
    const settings = {
      userInfo,
      interfaceLanguage,
    };
    localStorage.setItem('voicebot-settings', JSON.stringify(settings));
  }, [userInfo, interfaceLanguage]);

  const value = {
    userInfo,
    setUserInfo,
    interfaceLanguage,
    setInterfaceLanguage,
  };

  return (
    <SettingsContext.Provider value={value}>
      {children}
    </SettingsContext.Provider>
  );
};
